import React from 'react';
import ApolloClient from 'apollo-boost';
import { ApolloProvider } from '@apollo/react-hooks';
import Layout from '../../../components/layout';
import SEO from '../../../components/seo';
import { SettingProvider } from '../../../lib/context/settings';
import { AdsProvider } from '../../../lib/context/ads';
import { Col, Row, Collapse } from 'antd';
import { Title } from '../../../components/styles';
import { device } from '../../../lib/theme';
import styled from 'styled-components';

const { Panel } = Collapse;

const HeroContainer = styled(Row)`
  background-size: cover;
  background-position: right;
  background-repeat: no-repeat;
  height: 100px;
  margin: auto;
  h1 {
    max-width: 80%;
    font-size: 32px;
    line-height: 1.1;
  }

  @media ${device.laptop} {
    h1 {
      max-width: 60%;
      font-size: 56px;
      line-height: 1.2;
    }

    background-position: center center;
  }
`;

const client = new ApolloClient<any>({
  uri: `https://${process.env.GATSBY_WORDPRESS_URL_PATH}/graphql`,
  fetchOptions: {
    credentials: 'include',
  },
});

const AutoBuyingFAQs: React.FC<any> = () => {
  return (
    <ApolloProvider client={client as any}>
      <SettingProvider>
        <AdsProvider>
          <Layout key={`${typeof window}`}>
            <SEO
              title="Auto Buying FAQ"
              description="AAA Auto Buying offers concierge-level car buying services for AAA Members. Get the answers to your Auto Buying questions here!"
              canonicalUrl="/automotive/autobuying/faq"
            />

            <HeroContainer align="middle" justify="center">
              <Col xs={{ span: 22, offset: 1 }} md={{ span: 20, offset: 2 }}>
                <Title style={{ margin: 'auto' }}>AAA Auto Buying FAQ</Title>
              </Col>
            </HeroContainer>

            <Row style={{ paddingTop: 48, paddingBottom: 32 }}>
              <Col xs={{ span: 22, offset: 1 }} md={{ span: 20, offset: 2 }}>
                <p>
                  Have a question about AAA Auto Buying? Find your answers
                  below.
                </p>
                <Collapse defaultActiveKey={['1']} ghost>
                  <Panel
                    header="How does AAA Auto Buying Service work?"
                    key="1"
                  >
                    <p>
                      {' '}
                      We are a concierge car buying service for AAA Members.
                      With our team of automotive experts, you can completely
                      skip the hassle of a car dealership.
                    </p>
                    <p>
                      You can contact us by phone at 1-866-710-6226 or{' '}
                      <a href="https://tirestore.carolinas.aaa.com/auto-buying-quote/">
                        online
                      </a>{' '}
                      with your preferences, requirements and details and we’ll
                      handle your purchase from there. We have access to just
                      about any vehicle, new or pre-owned, and will shop our
                      network for the best value for you. We then present you
                      with options, and if accepted, move forward with the
                      purchase of the vehicle.{' '}
                    </p>
                    <p>
                      We will arrange to have the vehicle delivered to your door
                      with all necessary paperwork in-hand and can complete the
                      paperwork in your driveway or at your kitchen table. It’s
                      the easiest auto purchase you will ever make. We can also
                      handle financing on your behalf with our preferred
                      lenders, provide{' '}
                      <a href="https://carolinas.aaa.com/automotive/autobuying/extended-warranty/">
                        extended protection coverage
                      </a>{' '}
                      as well as{' '}
                      <a href="https://carolinas.aaa.com/automotive/autobuying/gap-auto-insurance/">
                        GAP insurance
                      </a>{' '}
                      at your request. We can also provide DMV concierge service
                      as needed so all you have to do is enjoy your new vehicle.
                    </p>
                  </Panel>
                  <Panel header="Can I test drive the car?" key="2">
                    <p>
                      When you select a vehicle that you like, we will proceed
                      to process your purchase. When the vehicle is delivered to
                      you, you will have more than just a test drive, you will
                      have a test own on any pre-owned vehicle we sell! You will
                      be provided up to 5 days or 250 miles to drive the
                      vehicle. If for any reason you decide you don’t love the
                      vehicle, simply return it for another vehicle. We will
                      pick that vehicle up and deliver another to you for no
                      additional fee. This benefit is available only for used
                      vehicles and is not valid with new vehicle purchases. For
                      more details on this please reach out to one of our auto
                      buying representatives.
                    </p>
                  </Panel>
                  <Panel header="Can I trade-in my current vehicle?" key="3">
                    <p>
                      Yes. We accept sight unseen trades after an appraisal with
                      you over the phone. We will also buy your vehicle from you
                      even if you don’t buy one from us. Just give us a call to
                      start the process.
                    </p>
                  </Panel>
                  <Panel
                    header="Are there any extra fees with using the service?"
                    key="4"
                  >
                    <p>
                      The concierge service itself comes at no additional fee
                      versus purchasing at a dealer. There are no upfront costs
                      for our service. Once you decide on a purchase, the only
                      fees in addition to the purchase price of the vehicle
                      would be a one-time document processing fee of $225, as
                      well as a delivery fee of only $1 per mile for the round
                      trip delivery. You may qualify for a free delivery, please
                      speak to a auto buying representative for more details.
                    </p>
                  </Panel>
                  <Panel header="Will I have to do any paperwork?" key="5">
                    <p>
                      All of the necessary paperwork involved in the sale will
                      be in the possession of the delivery driver. You can
                      complete the paperwork on your kitchen table and normally,
                      depending on questions you might have, it should take no
                      more than 30 minutes.
                    </p>
                  </Panel>
                  <Panel
                    header="How long does it take to find a car after I tell you what I want?"
                    key="6"
                  >
                    <ul>
                      <li>
                        <p>
                          New cars: Once you select the type of vehicle you
                          want, we work to find the best car at the best price.
                          We may visit new car showrooms and arrange a test
                          drive for you at a dealer nearest to your home. We can
                          also arrange delivery of the vehicle so you can test
                          drive it at your convenience. Depending on these
                          circumstances, the process from selection to delivery
                          can take anywhere from a few days to a few weeks.
                        </p>
                      </li>
                      <li>
                        <p>
                          Pre-owned cars: When you choose your car and we secure
                          it for you, we have it checked bumper to bumper at a
                          AAA Car Care Center to ensure all systems are
                          operating as intended. If there happens to be an
                          issue, you will never see that car! We want you to
                          have the assurance that the pre-owned vehicle you
                          selected is road-worthy and ready for use. You are
                          also welcome to test drive it at delivery.
                        </p>
                      </li>
                    </ul>
                  </Panel>
                  <Panel
                    header="How do I know that everything will be okay with my vehicle when I receive it?"
                    key="7"
                  >
                    <p>
                      All our pre-owned cars go through a thorough digital
                      multi-point vehicle inspection at one of our AAA Owned Car
                      Care locations and also come with a 3-month/3000-mile AAA
                      warranty. You also have the complimentary test own period
                      as well to make sure the vehicle is perfect for you. You
                      have up to 3 days and 150 miles to test own the vehicle.
                      If you don’t love it, simply return it for another
                      vehicle. Please speak to an auto buying representative for
                      additional details.
                    </p>
                  </Panel>
                  <Panel
                    header="Do I have to be a AAA Member to use the service?"
                    key="8"
                  >
                    <p>
                      Yes, you must be a AAA Member. However if you’re not, a
                      first year membership is free when you buy your car of
                      choice through AAA Auto Buying.
                    </p>
                  </Panel>
                  <Panel
                    header="How does delivery work to my location?"
                    key="9"
                  >
                    <p>
                      We have a team of drivers trained to deliver the cars to
                      your home or office. These team members will review the
                      car details with you at delivery.
                    </p>
                  </Panel>
                  <Panel
                    header="Are there financing options available?"
                    key="10"
                  >
                    <p>
                      Yes, we have a number of finance options available at
                      request. Already have financing lined up? Let us see if we
                      can get you a better deal. We have financing partners that
                      we have negotiated member specific deals with to save you
                      money!
                    </p>
                  </Panel>
                  <Panel
                    header="Have a question about our AAA Vehicle Extended Service Plans?"
                    key="11"
                  >
                    <p>
                      <a href="https://carolinas.aaa.com/automotive/autobuying/service-plans-faq/">
                        See our Extended Service Plan FAQ here
                      </a>
                      .
                    </p>
                  </Panel>
                </Collapse>
              </Col>
            </Row>
          </Layout>
        </AdsProvider>
      </SettingProvider>
    </ApolloProvider>
  );
};

export default AutoBuyingFAQs;
